import { FilterModel } from 'ag-grid-community';
import { omit } from 'lodash';
import { useEffect, useRef } from 'react';

import { useAgGridClientTableContext } from 'core/states';

const SETTLE_MS = 250;

interface IProps {
  // The catalog's and the filter page's choices as the grid's filters, by column: the locations,
  // the manufacturers, and each attribute's picked values or number.
  filterModel: FilterModel;
  // The columns the guided search speaks for: one it picked nothing for loses any filter it had
  // (one the grid remembered from an earlier visit, say), so the grid shows this search's choices.
  searchColumnIds: string[];
}

// Puts the catalog's and the filter page's choices on the grid's columns once the first rows are
// in, replacing whatever the search's columns had (a saved filter set, a stale one, a choice since
// dropped) and leaving the other columns alone. Set after the rows arrive rather than as initial
// state: a saved filter configuration would swallow initial state. A column a saved layout hides
// is shown, so no filter narrows the rows unseen, and the choices go on again when the columns
// change (Reset to Default Layout). More rows do not put them on again: the family loads page by
// page, and a filter the user changed meanwhile stays as the user left it.
export const PreAppliedFilters = ({ filterModel, searchColumnIds }: IProps) => {
  const { gridApi } = useAgGridClientTableContext();

  const appliedFor = useRef<string | null>(null);
  // The columns the last application filled, so a choice dropped since is taken off again.
  const appliedColumns = useRef<string[]>([]);

  const filterKey = JSON.stringify([filterModel, searchColumnIds]);

  useEffect(() => {
    if (!gridApi) {
      return undefined;
    }

    const apply = () => {
      if (gridApi.isDestroyed()) {
        return;
      }

      // Rows reach a client grid through transactions, so look at the nodes rather than rowData.
      let hasRows = false;

      gridApi.forEachNode(() => {
        hasRows = true;
      });

      const columns = Object.keys(filterModel).filter((colId) => gridApi.getColumn(colId));
      // Again for other choices or other columns, not for more rows.
      const applicationKey = `${filterKey}|${columns.join(',')}`;

      if (!hasRows || appliedFor.current === applicationKey) {
        return;
      }

      const rest = omit(gridApi.getFilterModel() ?? {}, [
        ...appliedColumns.current,
        ...searchColumnIds,
        ...columns,
      ]);

      appliedFor.current = applicationKey;
      appliedColumns.current = columns;
      gridApi.setFilterModel({
        ...rest,
        ...Object.fromEntries(columns.map((colId) => [colId, filterModel[colId]])),
      });
      gridApi.setColumnsVisible(
        columns.filter((colId) => !gridApi.getColumn(colId)?.isVisible()),
        true
      );
    };

    // Once the grid settles: the rows arrive in transactions and a saved layout sets the columns
    // again after them, hiding a column this has just shown.
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(apply, SETTLE_MS);
    };

    const onColumnsSet = () => {
      appliedFor.current = null;
      schedule();
    };

    schedule();
    gridApi.addEventListener('rowDataUpdated', schedule);
    gridApi.addEventListener('gridColumnsChanged', onColumnsSet);

    return () => {
      clearTimeout(timer);

      if (gridApi.isDestroyed()) {
        return;
      }

      gridApi.removeEventListener('rowDataUpdated', schedule);
      gridApi.removeEventListener('gridColumnsChanged', onColumnsSet);
    };
    // filterKey stands in for filterModel and searchColumnIds, which are rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, filterKey]);

  return null;
};
